import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { ROLES } from '../constants/roles';

const DemoModeContext = createContext(null);

// --- DemoModeContext (Phase 1) ---
//
// Two independent demo layers compose here:
//
// 1. Per-admin demo mode (existing): an admin toggles `enabled` for their own
//    session, picks `includedFarmIds`, and sets aliases. Aliases override the
//    real property/parcel names ONLY in that admin's session.
//
// 2. Customer-facing public demo (new): one or more farms have `isPublicDemo`
//    set to true. ALL signed-in users (free, paid, admin) can read those farms.
//    Optional global aliases live on the farm doc itself
//    (`publicDemoFarmAlias`, `publicDemoParcelAliases`) and apply to every
//    non-admin viewer.
//
// Composition rule: an admin's per-session aliases win for that admin's own
// session, even on a public demo property. Everyone else sees the public alias
// (if set) or the real name.

function getDefaultSettings() {
  return {
    enabled: false,
    includedFarmIds: [],
    farmAliases: {},
    parcelAliases: {},
  };
}

function toSafeString(value) {
  if (value == null) return '';
  return String(value).trim();
}

function toParcelKeyCandidates(parcelKey) {
  if (Array.isArray(parcelKey)) return parcelKey.map(toSafeString).filter(Boolean);
  const clean = toSafeString(parcelKey);
  return clean ? [clean] : [];
}

export function DemoModeProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [settings, setSettings] = useState(getDefaultSettings);
  const [publicDemoFarms, setPublicDemoFarms] = useState([]);

  useEffect(() => {
    let unsubscribeUserDoc = null;
    let unsubscribePublicDemo = null;
    // Has the current auth session's users/{uid} doc been observed at least
    // once with exists() === true? We use this to gate the public-demo
    // subscription — see attachPublicDemoListenerOnce below for why.
    let userDocReady = false;

    const cleanupPublicDemo = () => {
      if (unsubscribePublicDemo) {
        unsubscribePublicDemo();
        unsubscribePublicDemo = null;
      }
    };

    // Attach the `farms where isPublicDemo == true` listener exactly once
    // per auth session. Idempotent — additional calls while attached are
    // no-ops.
    //
    // Why we can't subscribe immediately on auth: the canReadFarm rule
    // calls get(/databases/$(db)/documents/users/$(uid)) and returns false
    // when that doc doesn't exist. On first-time Google SSO signup,
    // `loginWithGoogle()` (firebase.js) only writes users/{uid} AFTER
    // signInWithPopup resolves — so the auth-state event fires before the
    // user doc is in place, and any Firestore query attached at that
    // moment is rejected with permission_denied. Per the Firestore v9 SDK
    // contract such a listener is detached and not retried.
    //
    // Naive retry-with-backoff (the previous attempt) hit a vicious cycle:
    // each permission_denied appears to nudge the SDK into a token-refresh,
    // which fires onAuthStateChanged again, which resets the retry counter,
    // which schedules another attach, which fails the same way. The loop
    // can run hundreds of times in a few seconds without ever escaping
    // attempt 1/5.
    //
    // The clean fix is to remove the race entirely: subscribe to the user
    // doc first (always allowed by `match /users/{userId}` rule), and only
    // attach the public-demo listener once the user doc has fired its
    // first snapshot with exists() === true. By then the canReadFarm rule
    // has all the data it needs.
    const attachPublicDemoListenerOnce = () => {
      if (unsubscribePublicDemo) return;
      const publicDemoQ = query(collection(db, 'farms'), where('isPublicDemo', '==', true));
      unsubscribePublicDemo = onSnapshot(
        publicDemoQ,
        (snap) => {
          setPublicDemoFarms(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        },
        (err) => {
          // At this point the user doc exists, so this is a real error
          // (rules misconfigured, network, etc.) — not the signup race.
          // Log and fall back to an empty list so the rest of the app
          // still works.
          console.warn('DemoModeContext public demo listener failed:', err?.message || err);
          setPublicDemoFarms([]);
        }
      );
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }
      cleanupPublicDemo();
      userDocReady = false;

      if (!user) {
        setUid(null);
        setIsAdmin(false);
        setSettings(getDefaultSettings());
        setPublicDemoFarms([]);
        setLoading(false);
        return;
      }

      setUid(user.uid);
      const userRef = doc(db, 'users', user.uid);
      unsubscribeUserDoc = onSnapshot(
        userRef,
        (snap) => {
          if (snap.exists()) {
            // User doc exists — safe to subscribe to the public demo. On
            // first-time signup this fires twice: once with exists()=false
            // (loginWithGoogle hasn't run setDoc yet) and once with
            // exists()=true (after setDoc completes ~ms later). The
            // userDocReady gate ensures we only attach on the second fire.
            if (!userDocReady) {
              userDocReady = true;
              attachPublicDemoListenerOnce();
            }
            const data = snap.data() || {};
            const role = data.role || ROLES.USER;
            const nextIsAdmin = role === ROLES.ADMIN;
            setIsAdmin(nextIsAdmin);
            const stored = data.demoModeSettings || {};
            setSettings({
              enabled: !!stored.enabled,
              includedFarmIds: Array.isArray(stored.includedFarmIds) ? stored.includedFarmIds : [],
              farmAliases: stored.farmAliases && typeof stored.farmAliases === 'object' ? stored.farmAliases : {},
              parcelAliases: stored.parcelAliases && typeof stored.parcelAliases === 'object' ? stored.parcelAliases : {},
            });
            setLoading(false);
          } else {
            // User doc not in place yet — first-time signup race.
            // loginWithGoogle's setDoc will create it shortly and the
            // listener will refire above.
            setIsAdmin(false);
            setSettings(getDefaultSettings());
            setLoading(false);
          }
        },
        () => {
          setLoading(false);
        }
      );
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
      cleanupPublicDemo();
    };
  }, []);

  const saveSettings = useCallback(async (next) => {
    if (!uid || !isAdmin) return;
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, { demoModeSettings: next }, { merge: true });
  }, [uid, isAdmin]);

  const updateSettings = useCallback(async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await saveSettings(next);
    } catch (error) {
      console.error('Failed to save demo mode settings:', error);
      setSettings(settings);
      throw error;
    }
  }, [settings, saveSettings]);

  const setDemoEnabled = useCallback(async (enabled) => {
    if (!isAdmin) return;
    await updateSettings({ enabled: !!enabled });
  }, [isAdmin, updateSettings]);

  const setIncludedFarmIds = useCallback(async (includedFarmIds) => {
    if (!isAdmin) return;
    await updateSettings({ includedFarmIds: Array.isArray(includedFarmIds) ? includedFarmIds : [] });
  }, [isAdmin, updateSettings]);

  const setFarmAlias = useCallback(async (farmId, alias) => {
    if (!isAdmin || !farmId) return;
    const current = settings.farmAliases || {};
    const nextFarmAliases = { ...current };
    const clean = toSafeString(alias);
    if (clean) nextFarmAliases[farmId] = clean;
    else delete nextFarmAliases[farmId];
    await updateSettings({ farmAliases: nextFarmAliases });
  }, [isAdmin, settings.farmAliases, updateSettings]);

  const setParcelAlias = useCallback(async (farmId, parcelKey, alias) => {
    if (!isAdmin || !farmId || !parcelKey) return;
    const current = settings.parcelAliases || {};
    const farmAliases = { ...(current[farmId] || {}) };
    const clean = toSafeString(alias);
    if (clean) farmAliases[parcelKey] = clean;
    else delete farmAliases[parcelKey];
    await updateSettings({
      parcelAliases: {
        ...current,
        [farmId]: farmAliases,
      },
    });
  }, [isAdmin, settings.parcelAliases, updateSettings]);

  const isDemoModeEnabled = isAdmin && !!settings.enabled;

  // Public-demo lookup. publicDemoFarmIds is a Set so callers can do O(1) checks.
  const publicDemoFarmsById = useMemo(() => {
    const map = {};
    for (const f of publicDemoFarms) {
      if (f && f.id) map[f.id] = f;
    }
    return map;
  }, [publicDemoFarms]);

  const publicDemoFarmIds = useMemo(
    () => new Set(publicDemoFarms.map((f) => f.id).filter(Boolean)),
    [publicDemoFarms]
  );

  const isPublicDemoFarm = useCallback(
    (farmId) => !!farmId && publicDemoFarmIds.has(farmId),
    [publicDemoFarmIds]
  );

  const isFarmInDemoScope = useCallback((farmId) => {
    if (!isDemoModeEnabled) return true;
    const ids = settings.includedFarmIds || [];
    return ids.includes(farmId);
  }, [isDemoModeEnabled, settings.includedFarmIds]);

  const getDefaultFarmAlias = useCallback((farmId, fallbackIndex = 0) => {
    const ids = settings.includedFarmIds || [];
    const idx = ids.indexOf(farmId);
    if (idx >= 0) return `Property ${idx + 1}`;
    return `Property ${fallbackIndex + 1}`;
  }, [settings.includedFarmIds]);

  const getFarmDisplayName = useCallback((farm, fallbackIndex = 0) => {
    if (!farm) return '';
    const real = farm.farmName || farm.name || farm.id || '';

    // Admin's own per-session demo mode wins for them: even on a public demo
    // farm, an admin running a presentation sees their chosen alias.
    if (isDemoModeEnabled) {
      if (!isFarmInDemoScope(farm.id)) return 'Hidden Property';
      const alias = settings.farmAliases?.[farm.id];
      return alias || getDefaultFarmAlias(farm.id, fallbackIndex);
    }

    // Non-admin viewer (or admin without demo mode on) on a public demo farm:
    // use the global customer-facing alias if the admin has set one, else real.
    const publicFarm = publicDemoFarmsById[farm.id];
    if (publicFarm && publicFarm.publicDemoFarmAlias) {
      const cleaned = toSafeString(publicFarm.publicDemoFarmAlias);
      if (cleaned) return cleaned;
    }

    return real;
  }, [
    isDemoModeEnabled,
    isFarmInDemoScope,
    settings.farmAliases,
    getDefaultFarmAlias,
    publicDemoFarmsById,
  ]);

  const getParcelDisplayName = useCallback((farmId, parcelKey, fallbackName = '', fallbackIndex = 0) => {
    // Admin in demo mode: existing anonymisation path.
    if (isDemoModeEnabled) {
      if (!isFarmInDemoScope(farmId)) return 'Hidden Parcel';
      const candidates = toParcelKeyCandidates(parcelKey);
      if (candidates.length === 0) return `Parcel ${fallbackIndex + 1}`;
      const aliases = settings.parcelAliases?.[farmId] || {};
      for (const key of candidates) {
        const alias = aliases[key];
        if (alias) return alias;
      }
      const numericCandidate = candidates.find((key) => {
        const asNum = Number(key);
        return !Number.isNaN(asNum) && Number.isFinite(asNum) && asNum >= 0;
      });
      if (numericCandidate) {
        const asNum = Number(numericCandidate);
        return `Parcel ${Math.trunc(asNum) + 1}`;
      }
      return `Parcel ${fallbackIndex + 1}`;
    }

    // Non-admin viewer on a public demo farm: apply global parcel alias if set.
    const publicFarm = publicDemoFarmsById[farmId];
    if (publicFarm && publicFarm.publicDemoParcelAliases && typeof publicFarm.publicDemoParcelAliases === 'object') {
      const candidates = toParcelKeyCandidates(parcelKey);
      for (const key of candidates) {
        const alias = publicFarm.publicDemoParcelAliases[key];
        if (alias) {
          const cleaned = toSafeString(alias);
          if (cleaned) return cleaned;
        }
      }
    }

    return fallbackName || '';
  }, [
    isDemoModeEnabled,
    isFarmInDemoScope,
    settings.parcelAliases,
    publicDemoFarmsById,
  ]);

  // ---- Admin mutations for the customer-facing public demo layer ----
  // These write to the FARM doc (not the user doc) and require admin role.
  // Firestore rules guarantee that non-admins cannot flip these fields.

  const setFarmIsPublicDemo = useCallback(async (farmId, enabled) => {
    if (!isAdmin || !farmId) return;
    const farmRef = doc(db, 'farms', farmId);
    await updateDoc(farmRef, { isPublicDemo: !!enabled });
  }, [isAdmin]);

  const setPublicDemoFarmAlias = useCallback(async (farmId, alias) => {
    if (!isAdmin || !farmId) return;
    const farmRef = doc(db, 'farms', farmId);
    const cleaned = toSafeString(alias);
    await updateDoc(farmRef, { publicDemoFarmAlias: cleaned || null });
  }, [isAdmin]);

  const setPublicDemoParcelAlias = useCallback(async (farmId, parcelKey, alias) => {
    if (!isAdmin || !farmId || !parcelKey) return;
    const farmRef = doc(db, 'farms', farmId);
    const cleaned = toSafeString(alias);
    // We rewrite the whole map to keep behaviour predictable; the map is small.
    const farm = publicDemoFarmsById[farmId] || {};
    const current = (farm.publicDemoParcelAliases && typeof farm.publicDemoParcelAliases === 'object')
      ? { ...farm.publicDemoParcelAliases }
      : {};
    if (cleaned) current[parcelKey] = cleaned;
    else delete current[parcelKey];
    await updateDoc(farmRef, { publicDemoParcelAliases: current });
  }, [isAdmin, publicDemoFarmsById]);

  const value = useMemo(() => ({
    loading,
    isAdmin,
    settings,
    // Per-admin demo mode (existing)
    isDemoModeEnabled,
    isFarmInDemoScope,
    getFarmDisplayName,
    getParcelDisplayName,
    setDemoEnabled,
    setIncludedFarmIds,
    setFarmAlias,
    setParcelAlias,
    // Customer-facing public demo (new)
    publicDemoFarms,
    publicDemoFarmIds,
    isPublicDemoFarm,
    setFarmIsPublicDemo,
    setPublicDemoFarmAlias,
    setPublicDemoParcelAlias,
  }), [
    loading,
    isAdmin,
    settings,
    isDemoModeEnabled,
    isFarmInDemoScope,
    getFarmDisplayName,
    getParcelDisplayName,
    setDemoEnabled,
    setIncludedFarmIds,
    setFarmAlias,
    setParcelAlias,
    publicDemoFarms,
    publicDemoFarmIds,
    isPublicDemoFarm,
    setFarmIsPublicDemo,
    setPublicDemoFarmAlias,
    setPublicDemoParcelAlias,
  ]);

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  const ctx = useContext(DemoModeContext);
  if (!ctx) throw new Error('useDemoMode must be used within DemoModeProvider');
  return ctx;
}
